import {
	type Track,
	audioPlayer,
	isAudioPlayerEnabled,
	useAudioProgress,
} from "libs/audio-player";
import { useCallback, useEffect } from "react";
import type { Document } from "../types";
import { usePodcastPlayerStore } from "./usePodcastPlayerStore";
import { usePodcasts } from "./usePodcasts";

const usePodcastPlayer = () => {
	const progress = useAudioProgress();
	const isPlaying = usePodcastPlayerStore((store) => store.isPlaying);
	const setIsPlaying = usePodcastPlayerStore((store) => store.setIsPlaying);
	const setCurrentTrackInfo = usePodcastPlayerStore(
		(store) => store.setCurrentTrackInfo,
	);
	const currentTrackInfo = usePodcastPlayerStore(
		(store) => store.currentTrackInfo,
	);
	const setEndOfQueue = usePodcastPlayerStore((store) => store.setEndOfQueue);
	const setIsFirstTrack = usePodcastPlayerStore(
		(store) => store.setIsFirstTrack,
	);
	const setVisible = usePodcastPlayerStore((store) => store.setVisible);
	const { podcastsByType, podcasts } = usePodcasts();
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const checkEndOfQueue = async () => {
			const queue = await audioPlayer.getQueue();
			const currentTrackIndex = await audioPlayer.getActiveTrackIndex();
			setEndOfQueue(currentTrackIndex === queue.length - 1);
			setIsFirstTrack(currentTrackIndex === 0);
		};
		checkEndOfQueue();
	}, [currentTrackInfo]);

	const loadTracks = useCallback(
		async ({
			tracks,
			currentPodcastIndex,
			currentTrack,
			shouldReset = true,
		}: {
			tracks: Track[];
			currentPodcastIndex: number;
			currentTrack: Track;
			shouldReset?: boolean;
		}) => {
			if (!isAudioPlayerEnabled) {
				return;
			}

			setCurrentTrackInfo(currentTrack);

			const currentQueue = await audioPlayer.getQueue();
			if (!shouldReset && currentQueue.length > 0) {
				const activeIndex = await audioPlayer.getActiveTrackIndex();
				const activeTrack =
					activeIndex !== undefined
						? await audioPlayer.getTrack(activeIndex)
						: undefined;

				if (activeTrack?.url === currentTrack.url) {
					return;
				}

				const queueIndex = currentQueue.findIndex(
					(t) => t.url === currentTrack.url,
				);
				if (queueIndex >= 0) {
					await audioPlayer.skip(queueIndex);
					return;
				}
			}

			await audioPlayer.reset();
			await audioPlayer.add(tracks);
			await audioPlayer.skip(currentPodcastIndex);
		},
		[setCurrentTrackInfo],
	);

	const playTracks = async (tracks: Track[]) => {
		if (!isAudioPlayerEnabled) {
			return;
		}

		try {
			await audioPlayer.reset();
			await audioPlayer.add(tracks);
			await audioPlayer.play();

			setCurrentTrackInfo(tracks[0]);
			setVisible(true);
			setIsPlaying(true);
		} catch (error) {
			console.error("Error playing tracks:", error);
		}
	};

	const playPause = async () => {
		if (!isAudioPlayerEnabled) {
			return;
		}

		if (isPlaying) {
			await audioPlayer.pause();
			setIsPlaying(false);
		} else {
			await audioPlayer.play();
			setIsPlaying(true);
			setVisible(true);
		}
	};

	const skipToPrevious = async () => {
		if (!isAudioPlayerEnabled) {
			return;
		}

		await audioPlayer.skipToPrevious();
		const currentTrackIndex = await audioPlayer.getActiveTrackIndex();
		if (currentTrackIndex !== undefined) {
			const trackData = await audioPlayer.getTrack(currentTrackIndex);
			if (trackData) {
				setCurrentTrackInfo(trackData);
			}
			return trackData;
		}
	};

	const skipToNext = async () => {
		if (!isAudioPlayerEnabled) {
			return;
		}

		await audioPlayer.skipToNext();
		const currentTrackIndex = await audioPlayer.getActiveTrackIndex();
		if (currentTrackIndex !== undefined) {
			const trackData = await audioPlayer.getTrack(currentTrackIndex);
			if (trackData) {
				setCurrentTrackInfo(trackData);
			}
			return trackData;
		}
	};

	const seekTo = async (position: number) => {
		if (!isAudioPlayerEnabled) {
			return;
		}

		try {
			await audioPlayer.seekTo(position);
		} catch (error) {
			console.error("Error seeking to position:", error);
		}
	};

	const dismissPlayer = async () => {
		try {
			await audioPlayer.pause();
			await audioPlayer.reset();
			setVisible(false);
			setIsPlaying(false);
			setCurrentTrackInfo(null);
		} catch (error) {
			console.error("Error dismissing player:", error);
		}
	};

	const playCustomPodcasts = async (
		podcastId: number,
		customPodcastsIds: number[],
	) => {
		const podcastsToPlay: Document[] = [];
		const startIndex = customPodcastsIds.findIndex((p) => p === podcastId);

		for (let i = startIndex; i < podcasts.length; i++) {
			const podcast = podcasts.find((p) => p.id === customPodcastsIds[i]);
			if (podcast) {
				podcastsToPlay.push(podcast);
			}
		}

		const tracks: Track[] = podcastsToPlay.map((podcast) => ({
			url:
				podcast.metadata.podcast ||
				podcast.content.media?.find((media) => media.type === "AUDIO")?.url ||
				"",
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
			album: podcast.id.toString(),
		}));

		await playTracks(tracks);
	};

	const handleStartPlay = async (podcastId: number) => {
		const categoryIndex = podcastsByType.findIndex((category) =>
			category.podcasts.some((p) => p.id === Number(podcastId)),
		);

		if (categoryIndex === -1) {
			return;
		}

		const allPodcasts = podcastsByType
			.slice(categoryIndex)
			.flatMap((category) => category.podcasts);

		const podcastIndex = allPodcasts.findIndex(
			(p) => p.id === Number(podcastId),
		);

		const podcastsToPlay =
			podcastIndex !== -1 ? allPodcasts.slice(podcastIndex) : [];

		const tracks: Track[] = podcastsToPlay.map((podcast) => ({
			url:
				podcast.metadata.podcast ||
				podcast.content.media?.find((media) => media.type === "AUDIO")?.url ||
				"",
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
			album: podcast.id.toString(),
		}));

		await playTracks(tracks);
	};

	return {
		playTracks,
		isPlaying,
		playPause,
		skipToPrevious,
		skipToNext,
		seekTo,
		dismissPlayer,
		progress,
		handleStartPlay,
		playCustomPodcasts,
		loadTracks,
	};
};
export default usePodcastPlayer;
